/** #999 — position / end-of-track from exclusive engine (never GetTransportInfo). */

import type { ExclusivePlaybackState } from '../lib/connector';
import { useMaestroStore } from '../store/maestroStore';
import {
  normalizeExclusiveStreamError,
  shouldExclusivePollerAdvanceTrackEnd,
} from './exclusivePollerSignals';

export function resolveExclusiveEnginePositionWrite(input: {
  playbackState: string;
  storePosition: number;
  storeDuration: number;
  state: Pick<ExclusivePlaybackState, 'is_playing' | 'position_secs' | 'duration_secs'>;
}): { position: number; duration: number; shouldWrite: boolean } {
  const engineReporting =
    input.state.is_playing || input.state.position_secs > 0;
  const maySync =
    input.playbackState === 'playing'
    || input.playbackState === 'loading'
    || engineReporting;

  if (!maySync) {
    return {
      position: input.storePosition,
      duration: input.storeDuration,
      shouldWrite: false,
    };
  }

  const duration = input.storeDuration || input.state.duration_secs || 0;
  const position = engineReporting
    ? input.state.position_secs
    : input.storePosition;

  return { position, duration, shouldWrite: true };
}

export type ExclusiveEngineTransportTickResult = {
  streamError: string | null;
  shouldAdvance: boolean;
};

/**
 * One exclusive-engine poll tick — updates store position and decides EOS advance.
 * Shared by TransportCommand poller and TransportState stall reconcile (#999).
 */
export function evaluateExclusiveEngineTransportTick(
  state: ExclusivePlaybackState,
  endAdvancePending: boolean,
): ExclusiveEngineTransportTickResult {
  const store = useMaestroStore.getState();
  const streamError = normalizeExclusiveStreamError(state.stream_error);

  if (!streamError) {
    const write = resolveExclusiveEnginePositionWrite({
      playbackState: store.playbackState,
      storePosition: store.position || 0,
      storeDuration: store.duration || 0,
      state,
    });
    if (write.shouldWrite) {
      store.updateTime(write.position, write.duration);
    }
  }

  const effectiveDuration = store.duration || state.duration_secs || 0;
  const effectivePosition = (state.is_playing || state.position_secs > 0)
    ? state.position_secs
    : store.position;

  const shouldAdvance = !streamError
    && shouldExclusivePollerAdvanceTrackEnd({
      state,
      playbackState: store.playbackState,
      effectiveDuration,
      effectivePosition,
      endAdvancePending,
    });

  return { streamError, shouldAdvance };
}
